import React, {
  Dispatch, useContext, useEffect, useRef,
} from 'react';
import {
  FormControl,
  Input,
  InputLabel,
  Button,
  Box,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { DICTIONARY, SHOW_MODAL } from 'shared/constants';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import UserContext, { APP_AUTH_STATES } from 'shared/contexts/UserContext';
import AuthService from 'services/auth';
import NationalService from 'services/national';
import PersonService from 'services/person';
import { Role } from 'API';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface Props {
  dispatch: Dispatch<LoginAction>;
  state: LoginState;
}

const SignIn: React.FC<Props> = ({ dispatch, state }: Props) => {
  const [, userDispatch] = useContext(UserContext);

  const {
    authError, password, username, showPassword,
  } = state;

  const passwordInputRef = useRef<HTMLInputElement>(null);

  const handleTogglePasswordVisibility = () => {
    const input = passwordInputRef.current;
    const cursorPosition = input?.selectionStart ?? password.length;

    dispatch({ type: 'toggle password visibility' });

    requestAnimationFrame(() => {
      input?.setSelectionRange(cursorPosition, cursorPosition);
      input?.focus();
    });
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handlePasswordRequest = () => {
    dispatch({ type: 'request password reset' });
  };

  const handleSubmit = async () => {
    dispatch({ type: 'login' });

    try {
      let result;

      try {
        result = await signIn({ username, password });
      } catch (signInErr) {
        if ((signInErr as { name?: string })?.name === 'UserAlreadyAuthenticatedException') {
          await signOut();
          result = await signIn({ username, password });
        } else {
          throw signInErr;
        }
      }

      const { isSignedIn, nextStep } = result;

      // New password required challenge
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        dispatch({ type: 'challenge prompt' });

        return;
      }

      if (!isSignedIn) return;

      // Check email verification status after sign-in
      const attributes = await fetchUserAttributes();

      if (attributes.email_verified !== 'true') {
        dispatch({ type: 'verification prompt' });

        return;
      }

      const currentUser = await getCurrentUser();
      const person = await PersonService.getCurrent();
      const role = await AuthService.getRoleForCurrentUser();
      const {
        accessCompanies,
        adminCompanies,
      } = await AuthService.getCompaniesCurrentUserCanAccess();
      const dependents = role === Role.FOREIGNNATIONAL
        ? NationalService.getDependentIDs(person) : [];

      if (person) {
        PersonService.setPersonID(person.id);
        PersonService.setPersonType(person.__typename);
        await PersonService.updateHasLoggedIn(person);
        localStorage.setItem(SHOW_MODAL, 'true');
      }

      userDispatch({
        type: 'login',
        authState: APP_AUTH_STATES.SignedIn,
        authData: currentUser,
        person,
        role,
        accessCompanies,
        adminCompanies,
        dependents,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      dispatch({ type: 'auth error' });
    }
  };

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (
        (event.code === 'Enter' || event.code === 'NumpadEnter')
        && username
        && password
      ) {
        handleSubmit();
      }
    };

    window.addEventListener('keydown', listener);

    return () => {
      window.removeEventListener('keydown', listener);
    };
  }, [username, password]);

  return (
    <Box id="signin">
      <Typography sx={{ marginBottom: '2rem' }} variant="h5" component="h1">
        {DICTIONARY.SignInHeadline}
      </Typography>
      <form onSubmit={handleSubmit}>
        {authError && (
          <Typography className="error-text">{authError}</Typography>
        )}
        <FormControl sx={{ marginBottom: '2rem', width: '100%' }}>
          <InputLabel htmlFor="username-input">
            {DICTIONARY.EmailAddress}
          </InputLabel>
          <Input
            id="username-input"
            value={username}
            onChange={(e) => dispatch({
              type: 'field',
              fieldName: 'username',
              payload: e.currentTarget.value.trim(),
            })}
          />
        </FormControl>

        <FormControl sx={{ marginBottom: '2rem', width: '100%' }}>
          <InputLabel htmlFor="password-input">
            {DICTIONARY.Password}
          </InputLabel>
          <Input
            id="password-input"
            inputRef={passwordInputRef}
            type={showPassword ? 'text' : 'password'}
            value={password}
            autoComplete="current-password"
            onChange={(e) => dispatch({
              type: 'field',
              fieldName: 'password',
              payload: e.currentTarget.value.trim(),
            })}
            endAdornment={(
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleTogglePasswordVisibility}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  size="large">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )}
          />
        </FormControl>

        <Typography
          sx={(t) => ({ color: t.palette.textHint, fontSize: '0.75rem', marginBottom: '1rem' })}
        >
          Forgot your password?
          {' '}
          <button
            className="button button--inline"
            onClick={handlePasswordRequest}
            type="button"
          >
            {DICTIONARY.ResetPassword}
          </button>
        </Typography>

        <Box display="flex">
          <Button
            sx={{ marginLeft: 'auto' }}
            onClick={handleSubmit}
            variant="contained"
            color="primary"
          >
            {DICTIONARY.SignIn}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default SignIn;
